import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Form,
  Button,
  Container,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const InvoiceForm = ({
  CustomerId,
  customerName,
  setModalShow,
  setRefresh,
  setErrMsg,
}) => {
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksError, setBanksError] = useState("");
  const [banks, setBanks] = useState([]);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");

  const api = useMemo(() => {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });
  }, [token]);

  const [formData, setFormData] = useState({
    CustomerId,
    invoicePrefix: customerName.slice(0, 3).toUpperCase(),
    invoiceNumber: "",
    date: "",
    dueDate: "",
    desc: "",
    items: [{ description: "", units: "", unitPrice: "" }],
    total: 0,
    selectedBankId: "",
  });

  useEffect(() => {
    fetchUserBanks();
  }, []);

  const fetchUserBanks = async () => {
    try {
      setBanksLoading(true);
      setBanksError("");

      const res = await api.get("/users/me");
      const userBanks = res.data?.banks || [];

      setBanks(userBanks);

      if (userBanks.length > 0) {
        setFormData((prev) => ({
          ...prev,
          selectedBankId: String(userBanks[0].id),
        }));
      }
    } catch (error) {
      setBanksError(
        error?.response?.data?.error || "Failed to load payment details",
      );
    } finally {
      setBanksLoading(false);
    }
  };

  const formatToDDMMYYYY = (dateObj) => {
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const addDaysFormatted = (dateValue, days) => {
    if (!dateValue) return "";

    const date = new Date(dateValue + "T00:00:00");
    date.setDate(date.getDate() + days);

    return formatToDDMMYYYY(date);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "date") {
      setFormData((prev) => ({
        ...prev,
        date: value,
        dueDate: addDaysFormatted(value, 7),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [name]: value,
      };

      return {
        ...prev,
        items: newItems,
      };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: "", units: "", unitPrice: "" }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => {
      const units = parseFloat(item.units) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return total + units * price;
    }, 0);
  };

  const selectedBank = banks.find(
    (bank) => String(bank.id) === String(formData.selectedBankId),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const total = calculateTotal();

      await api.post("/invoices/create", {
        ...formData,
        total,
        paymentDetails: selectedBank
          ? {
              bankId: selectedBank.id,
              bankName: selectedBank.bankName,
              accountName: selectedBank.accountName,
              accountNumber: selectedBank.accountNumber,
              IBAN: selectedBank.IBAN,
              currency: selectedBank.currency,
            }
          : null,
      });

      setLoading(false);
      setModalShow(false);
      setRefresh(true);
      navigate(`/customer/${CustomerId}`);
    } catch (error) {
      setErrMsg(
        "Something went wrong. Please contact support and provide this error: '" +
          error.message +
          "'",
      );
      setLoading(false);
      navigate(`/customer/${CustomerId}`);
      setRefresh(true);
    }
  };

  if (loading) {
    return (
      <Container
        style={{ height: "100vh", fontSize: "250px" }}
        className="d-flex justify-content-center align-items-center"
      >
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "700px" }}>
      <h1>Create Invoice</h1>

      {banksError && <Alert variant="warning">{banksError}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label className="fw-bold mt-2">Customer</Form.Label>
          <Form.Control type="text" value={customerName} readOnly required />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold mt-2">Date</Form.Label>
              <Form.Control
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold mt-2">Due Date</Form.Label>
              <Form.Control
                type="text"
                name="dueDate"
                value={formData.dueDate}
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Invoice Number</Form.Label>
          <Form.Control
            type="text"
            name="invoiceNumber"
            value={formData.invoiceNumber}
            onChange={handleChange}
            placeholder="Leave blank for auto number"
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Invoice Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="desc"
            value={formData.desc}
            onChange={handleChange}
          />
        </Form.Group>

        <Card className="mt-3 mb-3">
          <Card.Body>
            <h5 className="mb-3">Payment Details</h5>

            <Form.Group>
              <Form.Label className="fw-bold">Select Bank Account</Form.Label>
              <Form.Select
                name="selectedBankId"
                value={formData.selectedBankId}
                onChange={handleChange}
                disabled={banksLoading || banks.length === 0}
              >
                {banks.length === 0 ? (
                  <option value="">No bank accounts available</option>
                ) : (
                  banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.bankName} - {bank.accountName} ({bank.currency})
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>

            {selectedBank && (
              <div className="mt-3">
                <p className="mb-1">
                  <strong>Bank Name:</strong> {selectedBank.bankName}
                </p>
                <p className="mb-1">
                  <strong>Account Name:</strong> {selectedBank.accountName}
                </p>
                <p className="mb-1">
                  <strong>Account Number:</strong> {selectedBank.accountNumber}
                </p>
                <p className="mb-1">
                  <strong>IBAN:</strong> {selectedBank.IBAN || "-"}
                </p>
                <p className="mb-0">
                  <strong>Currency:</strong> {selectedBank.currency}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>

        {formData.items.map((item, index) => (
          <div key={index} style={{ marginBottom: "1rem" }}>
            <Form.Group>
              <Form.Label className="fw-bold mt-2">Item Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={item.description}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-bold mt-2">Units</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="units"
                min={0}
                value={item.units}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-bold mt-2">Unit Price</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="unitPrice"
                min={0}
                value={item.unitPrice}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            {index > 0 && (
              <Button
                variant="danger"
                className="mt-2"
                type="button"
                onClick={() => removeItem(index)}
              >
                Remove Item
              </Button>
            )}
          </div>
        ))}

        <Button
          variant="secondary"
          className="mb-3"
          type="button"
          onClick={addItem}
        >
          Add Item
        </Button>

        <Form.Group>
          <Form.Label>Total</Form.Label>
          <Form.Control type="text" value={calculateTotal()} readOnly />
        </Form.Group>

        <Button
          className="mt-3"
          type="submit"
          disabled={
            banksLoading || (banks.length > 0 && !formData.selectedBankId)
          }
        >
          Create Invoice
        </Button>
      </Form>
    </Container>
  );
};

export default InvoiceForm;
